if (!customElements.get('product-style-videos')) {
  customElements.define(
    'product-style-videos',
    class ProductStyleVideos extends HTMLElement {
      connectedCallback() {
        // Guards against a duplicate init if the browser ever calls
        // connectedCallback more than once for the same node.
        if (this.initialized) return;
        this.initialized = true;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.visibility = new Map();
        this.listenedVideos = new WeakSet();
        this.pauseHandlers = new Map();
        this.resumeTimers = new Map();

        this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.boundHandleSectionLoad = this.handleSectionLoad.bind(this);

        this.refreshVideos();

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.visibility.set(entry.target, entry.isIntersecting);
                if (entry.isIntersecting) {
                  this.playVideo(entry.target);
                } else {
                  this.cancelResumeTimer(entry.target);
                  entry.target.pause();
                  if (this.reducedMotion) {
                    entry.target
                      .closest('[data-style-video-item]')
                      ?.querySelector('[data-play-toggle]')
                      ?.removeAttribute('hidden');
                  }
                }
              });
            },
            { threshold: 0.5 }
          );
          this.observeVideos();
        }

        document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);
        // Reconnects the observer/listeners when Shopify replaces this
        // section's markup (theme editor section re-render).
        document.addEventListener('shopify:section:load', this.boundHandleSectionLoad);

        // Dawn publishes this event after every color/size selection
        // (see assets/product-info.js, handleUpdateProductInfo). Reuses the
        // same reconnection strategy already proven for the "Porté par la
        // communauté" video_duo block in sections/main-product.liquid.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.handleVariantChange();
          });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
        document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
        document.removeEventListener('shopify:section:load', this.boundHandleSectionLoad);

        (this.videos || []).forEach((video) => {
          this.cancelResumeTimer(video);
          const handler = this.pauseHandlers.get(video);
          if (handler) video.removeEventListener('pause', handler);
        });
        this.resumeTimers.clear();
        this.pauseHandlers.clear();
        this.initialized = false;
      }

      handleVisibilityChange() {
        if (document.hidden) {
          (this.videos || []).forEach((video) => this.cancelResumeTimer(video));
        }
      }

      handleSectionLoad(event) {
        if (!event.target || !event.target.contains(this)) return;
        this.refreshVideos();
        this.observeVideos();
      }

      refreshVideos() {
        this.videos = Array.from(this.querySelectorAll('video'));

        this.videos.forEach((video) => {
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.removeAttribute('controls');
          if (!this.visibility.has(video)) this.visibility.set(video, false);

          if (!this.listenedVideos.has(video)) {
            const handler = () => this.handleVideoPause(video);
            video.addEventListener('pause', handler);
            this.pauseHandlers.set(video, handler);
            this.listenedVideos.add(video);
          }
        });

        this.querySelectorAll('[data-play-toggle]').forEach((button) => {
          if (button.dataset.bound) return;
          button.dataset.bound = 'true';
          button.addEventListener('click', () => this.handleManualPlay(button));
        });

        // prefers-reduced-motion: never autoplay; let people opt in per video.
        if (this.reducedMotion) {
          this.videos.forEach((video) => {
            video.pause();
            video
              .closest('[data-style-video-item]')
              ?.querySelector('[data-play-toggle]')
              ?.removeAttribute('hidden');
          });
        }
      }

      observeVideos() {
        if (!this.observer) return;
        this.observer.disconnect();
        this.videos.forEach((video) => this.observer.observe(video));
      }

      playVideo(video) {
        if (this.reducedMotion) return;
        video.play().catch(() => {});
      }

      // Fires whenever a video pauses for any reason, including Dawn's
      // global pauseAllMedia() (assets/global.js), which media-gallery.js
      // calls on every variant change that swaps the active product media.
      handleVideoPause(video) {
        if (this.reducedMotion) return;
        if (!this.visibility.get(video)) return;
        this.scheduleResume(video);
      }

      handleVariantChange() {
        this.refreshVideos();
        this.observeVideos();
        if (this.reducedMotion) return;
        this.videos.forEach((video) => {
          if (this.visibility.get(video)) this.scheduleResume(video);
        });
      }

      scheduleResume(video) {
        if (this.resumeTimers.has(video)) return; // one pending resume per video
        const timer = { raf1: null, raf2: null, timeoutId: null };
        this.resumeTimers.set(video, timer);

        timer.raf1 = requestAnimationFrame(() => {
          timer.raf2 = requestAnimationFrame(() => {
            timer.timeoutId = setTimeout(() => {
              this.resumeTimers.delete(video);
              this.attemptResume(video);
            }, 150);
          });
        });
      }

      attemptResume(video) {
        if (this.reducedMotion) return;
        if (!this.isConnected || !this.visibility.get(video) || document.hidden) return;
        video.play().catch(() => {});
      }

      cancelResumeTimer(video) {
        const timer = this.resumeTimers.get(video);
        if (!timer) return;
        if (timer.raf1) cancelAnimationFrame(timer.raf1);
        if (timer.raf2) cancelAnimationFrame(timer.raf2);
        if (timer.timeoutId) clearTimeout(timer.timeoutId);
        this.resumeTimers.delete(video);
      }

      handleManualPlay(button) {
        const video = button.closest('[data-style-video-item]')?.querySelector('video');
        if (!video) return;
        video.play().catch(() => {});
        button.setAttribute('hidden', '');
      }
    }
  );
}
